import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, publicAssetUrl, errorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CityChip } from "@/components/customer/CityChip";
import { HeartButton } from "@/components/customer/HeartButton";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Menu, Search, Store as StoreIcon, Target, Flame, MapPin, Zap, PartyPopper, Sparkles, LayoutGrid, Film, Bus, Home as HomeIcon, Briefcase } from "lucide-react";
import { requestGeolocation, setLocalLocation, getLocalLocation, patchMyLocation } from "@/lib/location";
import { CustomerBottomNav } from "@/components/customer/CustomerBottomNav";
import { PartnerShopCard } from "@/components/customer/PartnerShopCard";
import { AIAssistant } from "@/components/customer/AIAssistant";
import { VoiceCaptureButton } from "@/components/customer/VoiceCaptureButton";
import { CategoryDrawer } from "@/components/customer/CategoryDrawer";
import { PartnerBrandsRail } from "@/components/customer/PartnerBrandsRail";
import { CountdownStrip, EndingSoonBadge } from "@/components/posts/CountdownStrip";
import { trackEvent } from "@/lib/analytics";

// Rebrand v4 — shelf-style tile lineup per user's reference. All 12 icons
// share a uniform warm-orange render inside a `#FFF3E6` tint circle.
const CAT_ICONS = {
    "todays-top":   Target,
    "trending":     Flame,
    "near-me":      MapPin,
    "flash-deals":  Zap,
    "weekend":      PartyPopper,
    "festival":     Sparkles,
    "new-stores":   StoreIcon,
    "categories":   LayoutGrid,
    "movies":       Film,
    "bus-booking":  Bus,
    "real-estate":  HomeIcon,
    "jobs":         Briefcase,
};

const buildTiles = () => {
    // Shelf-style tile lineup restored per user's reference image. Routes
    // reuse existing backend endpoints — no backend changes required.
    return [
        { key: "todays-top",   label: "Today's Top", to: "/posts?sort=most_favorited&shelf=todays_top_offers" },
        { key: "trending",     label: "Trending",    to: "/posts?sort=most_favorited" },
        { key: "near-me",      label: "Near Me",     action: "geolocate" },
        { key: "flash-deals",  label: "Flash Deals", to: "/posts?shelf=flash_deals" },
        { key: "weekend",      label: "Weekend",     to: "/posts?shelf=weekend_specials" },
        { key: "festival",     label: "Festival",    to: "/posts?festival=true" },
        { key: "new-stores",   label: "New Stores",  to: "/posts?new_store=true" },
        { key: "categories",   label: "Categories",  action: "categories" },
        { key: "movies",       label: "Movies",      to: "/posts?type=MOVIE" },
        { key: "bus-booking",  label: "Bus Booking", to: "/customer/bus-booking" },
        { key: "real-estate",  label: "Real Estate", to: "/posts?type=EXHIBITION" },
        { key: "jobs",         label: "Jobs",        to: "/posts?type=JOB_FAIR" },
    ];
};

export default function CustomerHome() {
    const { user } = useAuth();
    const [sp] = useSearchParams();
    // Phase 10 — separate the district shown on the home surface (from URL, for
    // hero/sections/feeds) from the user's HOME district (used for every tile
    // target). Tiles must always take the user to *their* district, not the one
    // they happen to be browsing.
    const homeDistrict = user?.district_slug || getLocalLocation()?.district_slug || "ernakulam";
    const district = sp.get("district_slug") || homeDistrict;
    const [hero, setHero] = useState(null);
    const [sections, setSections] = useState(null);
    const [justAdded, setJustAdded] = useState([]);
    const [topShops, setTopShops] = useState([]);
    const [partnerShops, setPartnerShops] = useState([]);
    const [merchantHeroAds, setMerchantHeroAds] = useState([]);
    const [merchantStripAds, setMerchantStripAds] = useState([]);
    const [heroIdx, setHeroIdx] = useState(0);
    const [pause, setPause] = useState(false);
    const [catSheetOpen, setCatSheetOpen] = useState(false);
    const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const nav = useNavigate();
    const TILES = useMemo(() => buildTiles(), []);

    useEffect(() => {
        setHero(null); setSections(null); setJustAdded([]); setTopShops([]);
        Promise.all([
            api.get(`/customer-home/hero?district_slug=${district}&limit=6`).then((r) => setHero(r.data.items || [])).catch(() => setHero([])),
            api.get(`/customer-home/sections?district_slug=${district}`).then((r) => setSections(r.data)).catch(() => setSections({})),
            api.get(`/posts?district_slug=${district}&limit=10`).then((r) => setJustAdded(r.data || [])).catch(() => {}),
            api.get(`/trending?target_type=SHOP&district_slug=${district}&window=all&limit=5`).then((r) => setTopShops(r.data.items || [])).catch(() => {}),
            api.get(`/partner-shops?is_featured=true&limit=6`).then((r) => setPartnerShops(r.data?.items || [])).catch(() => setPartnerShops([])),
            // Phase 15b — merchant-submitted, admin-approved hero + strip ads.
            api.get(`/ads/hero?district_slug=${district}&limit=6`).then((r) => setMerchantHeroAds(r.data?.items || [])).catch(() => setMerchantHeroAds([])),
            api.get(`/ads/strip?district_slug=${district}&limit=4`).then((r) => setMerchantStripAds(r.data?.items || [])).catch(() => setMerchantStripAds([])),
        ]);
    }, [district]);

    useEffect(() => {
        api.get(`/categories`).then((r) => setCategories(r.data || [])).catch(() => setCategories([]));
    }, []);

    const handleTileClick = async (t) => {
        trackEvent("tile_click", { tile_slug: t.key, tile_label: t.label, target_route: t.to || t.action });
        if (t.action === "categories") { setCatSheetOpen(true); return; }
        if (t.action === "geolocate") {
            // Phase 9c — real GPS radius. Phase 10 — always route to the user's
            // HOME district (not the currently-browsed URL district).
            const loc = getLocalLocation();
            let lat = user?.location_lat ?? loc?.lat ?? null;
            let lng = user?.location_lng ?? loc?.lng ?? null;
            if (lat == null || lng == null) {
                try {
                    const gp = await requestGeolocation();
                    lat = gp.lat; lng = gp.lng;
                    setLocalLocation({ ...(loc || {}), lat, lng, district_slug: homeDistrict });
                    if (user) {
                        try { await patchMyLocation({ lat, lng, district_slug: homeDistrict, source: "GPS" }); } catch { /* ignore */ }
                    }
                } catch (e) {
                    toast.error("Location permission denied — showing district-wide results");
                    nav(`/posts?nearby=5km&district_slug=${encodeURIComponent(homeDistrict)}`);
                    return;
                }
            }
            nav(`/posts?nearby=5km&lat=${lat}&lng=${lng}&district_slug=${encodeURIComponent(homeDistrict)}`);
            return;
        }
        nav(t.to);
    };

    useEffect(() => {
        // Fire-and-forget home_view on mount so we can approximate DAU without
        // shipping a full analytics SDK.
        trackEvent("home_view", { district: homeDistrict });
    }, []);

    useEffect(() => {
        if (pause || !hero || hero.length < 2) return;
        // Rebrand v3 — 3s auto-slide
        const t = setInterval(() => setHeroIdx((i) => (i + 1) % hero.length), 3000);
        return () => clearInterval(t);
    }, [pause, hero]);

    return (
        <div className="min-h-screen bg-white pb-24 sm:pb-6" data-brand="sky" data-testid="customer-home">
            {/* Sticky header + search */}
            <header
                className="sticky top-0 z-40 border-b border-slate-200 bg-white"
                data-testid="home-header"
            >
                <div className="mx-auto grid max-w-6xl grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 pt-7 pb-2 md:px-8 md:pt-10 md:pb-3">
                    {/* Left slot — hamburger menu opens the Category Drawer */}
                    <div className="flex items-center">
                        <button
                            className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-slate-100"
                            aria-label="Browse categories"
                            data-testid="home-hamburger"
                            onClick={() => setCategoryDrawerOpen(true)}
                        >
                            <Menu size={22} className="text-neutral-700" />
                        </button>
                    </div>
                    {/* Centered logo — transparent-bg raster, mathematically centered */}
                    <Link
                        to={`/home?district_slug=${homeDistrict}`}
                        className="flex items-center justify-center"
                        data-testid="home-logo"
                    >
                        <img
                            src="/logo.png"
                            alt="OfferNest"
                            className="h-24 w-auto object-contain md:h-32"
                        />
                    </Link>
                    {/* Right slot — city chip */}
                    <div className="flex items-center justify-end">
                        <CityChip />
                    </div>
                </div>
                <div className="mx-auto max-w-6xl px-4 pt-2 pb-4 md:px-8 md:pt-3 md:pb-5">
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => nav(`/posts?district_slug=${homeDistrict}`)}
                            data-testid="home-search-bar"
                            className="flex flex-1 items-center gap-2 rounded-full border border-slate-200 bg-[#FFF8F2] px-4 py-2.5 text-left text-sm text-[var(--text-muted)] transition hover:border-[var(--brand-orange)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-orange)]"
                        >
                            <Search size={16} className="text-[var(--brand-orange)]" />
                            <span className="font-body">Search offers, stores…</span>
                        </button>
                        <VoiceCaptureButton
                            surface="home_search"
                            testId="search-mic-btn"
                            size="md"
                            onConfirm={(text) => nav(`/posts?search=${encodeURIComponent(text)}&district_slug=${encodeURIComponent(homeDistrict)}`)}
                        />
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-3 py-6 sm:px-4">
                {/* Hero carousel */}
                <section className="relative" data-testid="home-hero" onMouseEnter={() => setPause(true)} onMouseLeave={() => setPause(false)}>
                    {(() => {
                        // Phase 15b — prepend approved merchant hero ads before organic hero posts (cap at 6).
                        const merchantSlides = (merchantHeroAds || []).map((a) => ({
                            id: `mad-${a.id}`,
                            _merchant_ad_id: a.id,
                            _is_ad: true,
                            poster_image_url: a.artwork_url,
                            title: a.headline,
                            hero_reason: "Sponsored",
                            business_name: a.merchant_name || a.subline,
                            details: {},
                            cta_url: a.cta_url,
                        }));
                        const heroSlides = hero == null ? null : [...merchantSlides, ...hero].slice(0, 6);
                        if (heroSlides == null) {
                            return <Skeleton className="aspect-[4/3] w-full rounded-[24px] sm:aspect-[2/1]" />;
                        }
                        if (heroSlides.length === 0) {
                            return (
                                <div className="flex aspect-[4/3] items-center justify-center rounded-[24px] bg-white text-sm text-[var(--text-muted)] sm:aspect-[2/1]">
                                    No offers yet in {district}
                                </div>
                            );
                        }
                        return (
                        <div className="card-hero relative">
                            <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${heroIdx * 100}%)` }}>
                                {heroSlides.map((p) => {
                                    const to = p._is_ad ? (p.cta_url || `/posts?district_slug=${district}`) : `/posts/${p.id}`;
                                    const external = p._is_ad && p.cta_url && /^https?:/i.test(p.cta_url);
                                    const commonInner = (
                                        <>
                                            <img src={publicAssetUrl(p.poster_image_url)} alt={p.title} className="h-full w-full object-cover" />
                                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-white">
                                                <div className="font-heading text-[10px] font-semibold uppercase tracking-wider text-orange-300">{p.hero_reason}</div>
                                                <div className="mt-1 font-heading text-lg font-bold sm:text-2xl">{p.title}</div>
                                                <div className="mt-1 flex items-center justify-between">
                                                    {p.business_name && <span className="inline-flex items-center rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-semibold text-neutral-900" data-testid={`hero-shop-${p.id}`}>{p.business_name}</span>}
                                                    {typeof p.details?.discount_percent === "number" && (
                                                        <span className="savings-pill" data-testid={`hero-savings-${p.id}`}>
                                                            Save {p.details.discount_percent}%
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    );
                                    if (external) {
                                        return (
                                            <a
                                                key={p.id}
                                                href={to}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={() => api.post(`/ads/${p._merchant_ad_id}/track?kind=click`).catch(() => {})}
                                                className="relative aspect-[4/3] w-full shrink-0 sm:aspect-[2/1]"
                                                data-testid={`hero-slide-${p.id}`}
                                            >
                                                {commonInner}
                                            </a>
                                        );
                                    }
                                    return (
                                        <Link
                                            key={p.id}
                                            to={to}
                                            onClick={() => p._is_ad && api.post(`/ads/${p._merchant_ad_id}/track?kind=click`).catch(() => {})}
                                            className="relative aspect-[4/3] w-full shrink-0 sm:aspect-[2/1]"
                                            data-testid={`hero-slide-${p.id}`}
                                        >
                                            {commonInner}
                                        </Link>
                                    );
                                })}
                            </div>
                            <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1">
                                {heroSlides.map((_, i) => (
                                    <button key={i} onClick={() => setHeroIdx(i)} className={`h-1.5 rounded-full transition-all ${i === heroIdx ? "w-6 bg-[var(--brand-orange)]" : "w-1.5 bg-white/70"}`} aria-label={`slide ${i + 1}`} data-testid={`hero-dot-${i}`} />
                                ))}
                            </div>
                        </div>
                        );
                    })()}
                </section>

                {/* Category tiles — strict 4×3 on mobile, breathes wider on larger breakpoints */}
                <section className="mt-8" data-testid="home-tiles">
                    <div className="mb-3 flex items-center justify-between">
                        <h2 className="section-title">Explore</h2>
                    </div>
                    <div
                        className="grid grid-cols-4 gap-4 place-items-stretch md:grid-cols-6 md:gap-5 xl:grid-cols-12"
                        data-testid="home-tiles-grid"
                    >
                        {TILES.map((t) => {
                            const Icon = CAT_ICONS[t.key] || LayoutGrid;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => handleTileClick(t)}
                                    data-testid={`tile-${t.key}`}
                                    className="group flex flex-col items-center rounded-2xl p-1 text-center transition hover:-translate-y-0.5"
                                >
                                    <span
                                        aria-hidden
                                        className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FFF3E6] ring-1 ring-[#FED7AA]/60 shadow-sm transition group-hover:bg-[#FED7AA] md:h-16 md:w-16"
                                    >
                                        <Icon
                                            className="h-8 w-8 text-[var(--brand-orange)] md:h-10 md:w-10"
                                            strokeWidth={1.75}
                                        />
                                    </span>
                                    <span className="mt-2 font-heading text-xs font-medium leading-tight text-center text-[var(--text-primary)] line-clamp-2 md:text-sm">
                                        {t.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </section>

                {/* Phase 16 — One-Stop Hub partner rail */}
                <PartnerBrandsRail />

                {/* Rebrand v3 — Featured Offers strip ads (approved merchant ads first, then top organic offers) */}
                {(justAdded.length > 0 || merchantStripAds.length > 0) && (
                    <section className="mt-8" data-testid="home-strip-ads">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="font-heading text-lg font-bold text-[var(--text-primary)] md:text-xl">Featured Offers</h2>
                        </div>
                        <div className="mx-auto flex max-w-[640px] flex-col gap-3">
                            {(() => {
                                // Normalise merchant strip ads to the same shape used by the strip renderer.
                                const merchantStripSlides = (merchantStripAds || []).map((a) => ({
                                    id: `mad-strip-${a.id}`,
                                    _is_ad: true,
                                    _merchant_ad_id: a.id,
                                    _cta_url: a.cta_url,
                                    title: a.headline,
                                    business_name: a.merchant_name || "Sponsored",
                                    category_slug: "sponsored",
                                    poster_image_url: a.artwork_url,
                                    details: {},
                                }));
                                return [...merchantStripSlides, ...justAdded].slice(0, 4);
                            })().map((p, idx) => {
                                const accents = ["#F97316", "#10B981", "#14B8A6", "#38BDF8"];
                                const accent = accents[idx % accents.length];
                                const disc = p.details?.discount_percent;
                                const headline = typeof disc === "number" ? `${disc}% OFF` : (p.title || "SPECIAL DEAL");
                                const sub = p.title?.length && typeof disc === "number" ? p.title : "Valid Today";
                                const cat = (p.category_slug || "Deal").replace(/-/g, " ");
                                // For merchant ads, route to their cta_url (external if http, else internal) and fire an impression/click.
                                const isAd = !!p._is_ad;
                                const toHref = isAd ? (p._cta_url || `/posts?district_slug=${district}`) : `/posts/${p.id}`;
                                const isExternal = isAd && p._cta_url && /^https?:/i.test(p._cta_url);
                                const onTap = () => {
                                    if (isAd) {
                                        api.post(`/ads/${p._merchant_ad_id}/track?kind=click`).catch(() => {});
                                    } else {
                                        trackEvent("strip_ad_click", { strip_index: idx, post_id: p.id });
                                    }
                                };
                                const WrapperTag = isExternal ? "a" : Link;
                                const wrapperProps = isExternal
                                    ? { href: toHref, target: "_blank", rel: "noopener noreferrer" }
                                    : { to: toHref };
                                return (
                                    <WrapperTag
                                        key={p.id}
                                        {...wrapperProps}
                                        onClick={onTap}
                                        data-testid={`strip-ad-${idx}`}
                                        className="card-soft !p-0 overflow-hidden transition hover:-translate-y-0.5 active:scale-[0.99]"
                                    >
                                        {/* Colored strip */}
                                        <div
                                            className="relative flex items-center gap-3 p-4"
                                            style={{ background: accent }}
                                        >
                                            <div className="flex-1 min-w-0 text-white">
                                                <div className="font-heading text-[10px] font-semibold uppercase tracking-wider text-white/90 capitalize">{cat}</div>
                                                <div className="mt-1 font-heading text-3xl font-extrabold leading-none md:text-4xl">{headline}</div>
                                                <div className="mt-1 truncate font-body text-xs text-white/90 md:text-sm">{sub}</div>
                                            </div>
                                            <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-white/10 ring-1 ring-white/30 md:h-24 md:w-24">
                                                {p.poster_image_url && (
                                                    <img src={publicAssetUrl(p.poster_image_url)} alt="" className="h-full w-full object-cover" />
                                                )}
                                                {typeof disc === "number" && (
                                                    <span className="absolute right-1 top-1 rounded-full bg-black/70 px-1.5 py-0.5 font-heading text-[10px] font-bold text-white">
                                                        {disc}%
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        {/* Shop lockup + CTA */}
                                        <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white p-3">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FFF3E6] font-heading text-sm font-bold text-[var(--brand-orange)]">
                                                    {(p.business_name || "?").charAt(0).toUpperCase()}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="truncate font-heading text-sm font-semibold text-[var(--text-primary)]">{p.business_name || "Featured Shop"}</div>
                                                    <div className="truncate font-body text-[11px] text-[var(--text-muted)] capitalize">{cat}</div>
                                                </div>
                                            </div>
                                            <span
                                                className="btn-primary shrink-0 !py-1.5 !px-4 text-xs"
                                                data-testid={`strip-ad-cta-${idx}`}
                                            >
                                                GET COUPON
                                            </span>
                                        </div>
                                    </WrapperTag>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Rebrand v3 — Sponsored 2×2 square grid (paid slots).
                 * Distinct from the strip ads above: coexisting ad format that
                 * uses posts 4-7 of the same feed so both slots have data
                 * without needing new endpoints. */}
                {justAdded.length > 4 && (
                    <section className="mt-8" data-testid="home-sponsored">
                        <div className="mb-3 flex items-center justify-between">
                            <h2 className="font-heading text-lg font-bold text-[var(--text-primary)] md:text-xl">Sponsored</h2>
                        </div>
                        <div
                            className="mx-auto grid max-w-[640px] grid-cols-2 gap-3 md:max-w-full md:grid-cols-4 md:gap-4"
                            data-testid="home-sponsored-grid"
                        >
                            {justAdded.slice(4, 8).map((p, idx) => {
                                const disc = p.details?.discount_percent;
                                return (
                                    <Link
                                        key={p.id}
                                        to={`/posts/${p.id}`}
                                        onClick={() => trackEvent("sponsored_ad_click", { index: idx, post_id: p.id })}
                                        data-testid={`sponsored-card-${idx}`}
                                        className="card-soft !p-0 relative aspect-square overflow-hidden transition hover:-translate-y-0.5 active:scale-[0.99]"
                                    >
                                        {/* Poster */}
                                        <div className="absolute inset-0 bg-slate-100">
                                            {p.poster_image_url && (
                                                <img
                                                    src={publicAssetUrl(p.poster_image_url)}
                                                    alt={p.title || ""}
                                                    className="h-full w-full object-cover"
                                                />
                                            )}
                                        </div>
                                        {/* AD chip top-left */}
                                        <span
                                            className="status-pill status-pill-neutral absolute left-2 top-2 !text-[9px] !py-0.5 !px-2"
                                            aria-label="Sponsored"
                                        >
                                            AD
                                        </span>
                                        {/* Ending Soon badge (< 48h) */}
                                        {p.type === "OFFER" && p.valid_to && (
                                            <EndingSoonBadge
                                                validTo={p.valid_to}
                                                className="absolute left-2 top-8"
                                                testid={`sponsored-ending-soon-${idx}`}
                                            />
                                        )}
                                        {/* Discount pill top-right */}
                                        {typeof disc === "number" && (
                                            <span className="absolute right-2 top-2 rounded-full bg-[var(--brand-orange)] px-2 py-0.5 font-heading text-[10px] font-bold text-white">
                                                {disc}% OFF
                                            </span>
                                        )}
                                        {/* Live countdown strip (bottom) */}
                                        {p.type === "OFFER" && p.valid_to && (
                                            <CountdownStrip
                                                validTo={p.valid_to}
                                                size="sm"
                                                testid={`sponsored-countdown-${idx}`}
                                            />
                                        )}
                                        {/* Bottom gradient + shop name (lifted above the countdown strip when present) */}
                                        <div className={`absolute inset-x-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-2 pt-6 ${(p.type === "OFFER" && p.valid_to) ? "bottom-7" : "bottom-0"}`}>
                                            <div className="truncate font-heading text-xs font-semibold text-white md:text-sm">
                                                {p.business_name || "Featured Shop"}
                                            </div>
                                            <div className="truncate font-body text-[10px] text-white/80">
                                                {p.title}
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Kerala Partner Shops rail — sourced from admin master list.
                 * Renders 6 featured brands horizontally on mobile, 6-col grid
                 * on desktop. See parity audit — mirrors the mobile home rail. */}
                {partnerShops.length > 0 && (
                    <section className="mt-8" data-testid="home-partner-shops-rail">
                        <div className="mb-3 flex items-end justify-between">
                            <div>
                                <h2 className="font-heading text-lg font-bold text-[var(--text-primary)] md:text-xl">
                                    Kerala Partner Shops
                                </h2>
                                <p className="font-body text-xs text-[var(--text-muted)] md:text-sm">
                                    Featured retail brands near you.
                                </p>
                            </div>
                            <Link
                                to="/partner-shops"
                                className="section-link"
                                data-testid="home-partner-shops-see-all"
                            >
                                See all →
                            </Link>
                        </div>
                        {/* Mobile: horizontal snap scroller; Desktop (md+): 6-col grid */}
                        <div className="-mx-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-3 pb-2 md:mx-0 md:grid md:grid-cols-6 md:gap-4 md:overflow-visible md:px-0">
                            {partnerShops.map((b) => (
                                <div key={b.id} className="w-40 shrink-0 snap-start md:w-auto">
                                    <PartnerShopCard brand={b} source="partner_shops_home" />
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Just Added */}
                <section className="mt-8" data-testid="home-just-added">
                    <div className="mb-3 flex items-center justify-between">
                        <h2 className="section-title">Just Added</h2>
                        <Link to={`/posts?district_slug=${district}`} className="section-link" data-testid="just-added-view-all">View All →</Link>
                    </div>
                    <div className="-mx-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-3 pb-2">
                        {justAdded.slice(0, 10).map((p) => (
                            <Link key={p.id} to={`/posts/${p.id}`} className="card-soft relative w-44 shrink-0 snap-start !p-0 overflow-hidden" data-testid={`just-added-${p.id}`}>
                                <div className="relative aspect-[4/5] overflow-hidden bg-neutral-100">
                                    <img src={publicAssetUrl(p.poster_image_url)} alt={p.title} className="h-full w-full object-cover" />
                                    {p.type === "OFFER" && p.valid_to && (
                                        <CountdownStrip
                                            validTo={p.valid_to}
                                            size="sm"
                                            testid={`just-added-countdown-${p.id}`}
                                        />
                                    )}
                                </div>
                                <div className="p-3">
                                    <div className="truncate font-heading text-sm font-semibold text-[var(--text-primary)]">{p.title}</div>
                                    <div className="mt-2 flex items-center justify-between">
                                        {p.business_name && <span className="truncate text-[11px] text-[var(--text-muted)]">{p.business_name}</span>}
                                        {typeof p.details?.discount_percent === "number" && (
                                            <span className="savings-pill">-{p.details.discount_percent}%</span>
                                        )}
                                    </div>
                                </div>
                                <div className="absolute right-2 top-2 flex flex-col items-end gap-1">
                                    {p.type === "OFFER" && (
                                        <EndingSoonBadge validTo={p.valid_to} testid={`just-added-ending-soon-${p.id}`} />
                                    )}
                                    <HeartButton post={p} size={14} showCount={false} />
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>

                {/* Top shops */}
                <section className="mt-8" data-testid="home-top-shops">
                    <div className="mb-3 flex items-center justify-between">
                        <h2 className="section-title">Top Shops in <span className="capitalize">{district}</span></h2>
                        <Link to={`/posts?district_slug=${district}`} className="section-link" data-testid="top-shops-view-all">View All →</Link>
                    </div>
                    <div className="-mx-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-3 pb-2">
                        {topShops.map((s) => (
                            <Link key={s.shop_id} to={`/posts?district_slug=${district}`} className="card-soft flex w-52 shrink-0 snap-start items-center gap-3" data-testid={`shop-card-${s.shop_id}`}>
                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 font-heading text-lg font-bold text-[var(--brand-orange)]">
                                    {(s.business_name || "?").charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="truncate font-heading text-sm font-semibold text-[var(--text-primary)]">{s.business_name}</div>
                                    <div className="truncate text-[11px] text-[var(--text-muted)] capitalize">{(s.business_category || "").replace(/_/g, " ")}</div>
                                </div>
                                <span className="savings-pill text-xs">❤ {s.favorite_count_in_window}</span>
                            </Link>
                        ))}
                    </div>
                </section>

                {/* Phase 13b — Deep-blue Merchant CTA banner */}
                <section
                    data-testid="home-merchant-cta"
                    className="mt-8 flex flex-col items-start gap-4 rounded-[20px] p-6 text-white shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-8"
                    style={{ background: "var(--brand-blue-deep)" }}
                >
                    <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15">
                            <StoreIcon size={22} />
                        </div>
                        <div>
                            <h3 className="font-heading text-lg font-bold sm:text-xl">Own a shop in Kerala?</h3>
                            <p className="mt-1 text-sm text-white/80">List your offers free — reach customers near you.</p>
                        </div>
                    </div>
                    <Link
                        to="/merchant/register"
                        onClick={() => trackEvent("merchant_cta_click", { src: "home_banner" })}
                        data-testid="home-merchant-cta-btn"
                        className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-5 py-2.5 font-heading text-sm font-semibold text-[var(--brand-blue-deep)] shadow hover:bg-white/95"
                    >
                        Become a Merchant →
                    </Link>
                </section>


                <footer className="mt-10 border-t border-slate-200 pt-6 text-center text-[11px] text-[var(--text-muted)]">
                    <img src="/logo.png" alt="" className="mx-auto mb-2 h-10 opacity-70" />
                    <div>© {new Date().getFullYear()} OfferNest · <Link to="/merchant/register" className="font-heading font-semibold text-[var(--brand-orange)] hover:underline">Merchant? Sign up here</Link></div>
                </footer>
            </main>

            {/* Bottom nav (mobile + tablet) */}
            <CustomerBottomNav homeHref={`/home?district_slug=${district}`} />

            {/* Floating AI assistant */}
            <AIAssistant />

            {/* Category drawer (left slide) */}
            <CategoryDrawer open={categoryDrawerOpen} onOpenChange={setCategoryDrawerOpen} />

            {/* All Categories sheet */}
            <Sheet open={catSheetOpen} onOpenChange={setCatSheetOpen}>
                <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-3xl bg-white" data-testid="categories-sheet">
                    <SheetHeader className="text-left">
                        <SheetTitle className="font-heading text-xl font-bold text-[var(--text-primary)]">All Categories</SheetTitle>
                    </SheetHeader>
                    <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4">
                        {categories.map((c) => (
                            <button
                                key={c.slug}
                                onClick={() => { setCatSheetOpen(false); nav(`/posts?category_slug=${encodeURIComponent(c.slug)}&district_slug=${encodeURIComponent(homeDistrict)}`); }}
                                data-testid={`cat-sheet-${c.slug}`}
                                className="tile-grad flex flex-col items-center gap-2 rounded-2xl p-3 text-center"
                                style={{ "--tile": "#FF6B00" }}
                            >
                                <div className="text-3xl">{c.emoji || "🛍️"}</div>
                                <div className="font-heading text-xs font-semibold text-[var(--text-primary)]">{c.name}</div>
                            </button>
                        ))}
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}
